import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Crown, Flame, Sparkles, Calendar, Send, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SDCLoungePage = () => {
  const [requests, setRequests] = useState({ hot_seat_requests: [], concierge_requests: [] });
  const [loading, setLoading] = useState(true);
  const [hotSeatForm, setHotSeatForm] = useState({ topic: '', description: '', preferred_date: '' });
  const [conciergeForm, setConciergeForm] = useState({ request_type: '', description: '', urgency: 'normal' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sdc/requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHotSeatSubmit = async (e) => {
    e.preventDefault();
    if (!hotSeatForm.topic || !hotSeatForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sdc/hot-seat`, hotSeatForm);
      toast.success('Hot seat request submitted!');
      setHotSeatForm({ topic: '', description: '', preferred_date: '' });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConciergeSubmit = async (e) => {
    e.preventDefault();
    if (!conciergeForm.request_type || !conciergeForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sdc/concierge`, conciergeForm);
      toast.success('Concierge request submitted!');
      setConciergeForm({ request_type: '', description: '', urgency: 'normal' });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="sdc-lounge-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-terracotta flex items-center justify-center">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-forest">SDC VIP Lounge</h1>
              <Badge className="bg-terracotta text-white">VIP Access</Badge>
            </div>
          </div>
          <p className="text-gray-600 mt-2">Your exclusive VIP space for hot seats, concierge requests, and VIP events.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Hot Seat Request */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Flame className="h-5 w-5 text-terracotta" />
                Hot Seat Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                Submit a topic for a live hot seat session with Crystal & Dr. Nicole.
              </p>
              <form onSubmit={handleHotSeatSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="topic">Topic *</Label>
                  <Input
                    id="topic"
                    placeholder="e.g., Negotiating a board seat"
                    value={hotSeatForm.topic}
                    onChange={(e) => setHotSeatForm(prev => ({ ...prev, topic: e.target.value }))}
                    className="input-base mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your situation and what you'd like to explore..."
                    value={hotSeatForm.description}
                    onChange={(e) => setHotSeatForm(prev => ({ ...prev, description: e.target.value }))}
                    className="input-base mt-1 min-h-24"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Preferred Date (optional)</Label>
                  <Input
                    id="date"
                    type="date"
                    value={hotSeatForm.preferred_date}
                    onChange={(e) => setHotSeatForm(prev => ({ ...prev, preferred_date: e.target.value }))}
                    className="input-base mt-1"
                  />
                </div>
                <Button type="submit" className="w-full btn-secondary" disabled={submitting}>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Hot Seat Request
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Concierge Request */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-terracotta" />
                Concierge Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm mb-4">
                Need something special? Our concierge team is here to help.
              </p>
              <form onSubmit={handleConciergeSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="request_type">Request Type *</Label>
                  <Select 
                    value={conciergeForm.request_type} 
                    onValueChange={(value) => setConciergeForm(prev => ({ ...prev, request_type: value }))}
                  >
                    <SelectTrigger className="input-base mt-1">
                      <SelectValue placeholder="Select request type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="introduction">Introduction Request</SelectItem>
                      <SelectItem value="event">Event Coordination</SelectItem>
                      <SelectItem value="resource">Resource Request</SelectItem>
                      <SelectItem value="scheduling">Scheduling Assistance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="concierge_description">Description *</Label>
                  <Textarea
                    id="concierge_description"
                    placeholder="Describe your request in detail..."
                    value={conciergeForm.description}
                    onChange={(e) => setConciergeForm(prev => ({ ...prev, description: e.target.value }))}
                    className="input-base mt-1 min-h-24"
                  />
                </div>
                <div>
                  <Label htmlFor="urgency">Urgency</Label>
                  <Select 
                    value={conciergeForm.urgency} 
                    onValueChange={(value) => setConciergeForm(prev => ({ ...prev, urgency: value }))}
                  >
                    <SelectTrigger className="input-base mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - When you have time</SelectItem>
                      <SelectItem value="normal">Normal - Within a few days</SelectItem>
                      <SelectItem value="high">High - Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Concierge Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Request History */}
        <Card className="card-base mt-8">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest">Your Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto"></div>
              </div>
            ) : (requests.hot_seat_requests.length === 0 && requests.concierge_requests.length === 0) ? (
              <p className="text-gray-500 text-center py-8">No requests yet. Submit your first request above!</p>
            ) : (
              <div className="space-y-4">
                {requests.hot_seat_requests.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                        <Flame className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">{req.topic}</p>
                        <p className="text-sm text-gray-500">Hot Seat Request</p>
                      </div>
                    </div>
                    <Badge className={
                      req.status === 'approved' ? 'bg-green-100 text-green-700' :
                      req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {req.status}
                    </Badge>
                  </div>
                ))}
                {requests.concierge_requests.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-forest" />
                      </div>
                      <div>
                        <p className="font-medium text-forest capitalize">{req.request_type}</p>
                        <p className="text-sm text-gray-500">Concierge Request</p>
                      </div>
                    </div>
                    <Badge className={
                      req.status === 'completed' ? 'bg-green-100 text-green-700' :
                      req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {req.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* VIP Events */}
        <Card className="card-base mt-8">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Calendar className="h-5 w-5 text-terracotta" />
              VIP Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-forest text-white rounded-xl p-6 text-center">
              <Crown className="h-12 w-12 mx-auto mb-4 text-terracotta" />
              <h3 className="font-heading text-xl font-semibold mb-2">Exclusive VIP Calendar</h3>
              <p className="text-white/80 mb-4">
                Private events, hot seat sessions, and VIP-only gatherings.
              </p>
              <Button className="bg-terracotta text-white hover:bg-terracotta/90">
                View VIP Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SDCLoungePage;
